import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  Star, Search, Trash2, ChevronDown, ChevronUp, RefreshCw,
} from 'lucide-react';

import { claimsMultiVetRoutines } from '../BaseURL';

// ============================================================================
//  Claim Routines — the shelf, with a room of its own
// ----------------------------------------------------------------------------
//  A ROUTINE is a basket that has been applied to a group of claims: a diagnosis, its
//  labs, its drugs, sometimes a procedure. Multi Vet saves one under the pattern that
//  built it — "Out-patient · OPDC · Under 1 · Male · Malaria" — every time it is applied,
//  and the officer can promote one to PREMIUM.
//
//  ── WHY THIS SCREEN EXISTS ──────────────────────────────────────────────────
//
//  The shelf used to sit inside Multi Vet, above a list that can hold 1,317 distinct
//  diagnoses. It pushed the thing being searched further down the column, and it was
//  removed on request. But the routines behind it are the facility's accumulated
//  treatment standards — the most reusable thing this whole system produces — and they
//  had nowhere to be looked at, compared, promoted or thrown away.
//
//  So they get a panel. This one is a LIBRARY, not a picker: it does not apply anything.
//  Applying is Multi Vet's job, because that needs a group of claims to apply to and
//  this screen has none.
//
//  ── WHAT IT SHOWS THAT MULTI VET COULD NOT ──────────────────────────────────
//
//  Everything, including the DRAFTS — baskets saved and never applied. Multi Vet's own
//  shelf deliberately hides those: a shelf that offers unproven routines beside proven
//  ones makes the officer check each one before trusting it. Here the opposite is true —
//  this is where you go to see what exists, so `?all=1` and the state is a column.
// ============================================================================

//  Matches the tokens the rest of Claims uses, so this screen does not read as a
//  different application. Kept local rather than imported: ClaimsMenu's BRAND is not
//  exported, and duplicating five hex values is better than widening its surface.
const T = {
  ink: '#0F172A',
  inkSoft: '#334155',
  inkMuted: '#64748B',
  inkFaint: '#94A3B8',
  line: '#E2E8F0',
  head: '#F1F5F9',
  canvas: '#F8FAFC',
  green: '#0F5132',
  greenSoft: '#E7F3EC',
  danger: '#B42318',
  dangerSoft: '#FEF3F2',
  caution: '#8A5510',
};

const MONO = {
  fontFamily: 'ui-monospace, "Cascadia Mono", Consolas, monospace',
  fontVariantNumeric: 'tabular-nums',
};

const KIND_LABEL = {
  diagnosis: 'Diagnosis',
  procedure: 'Procedure',
  investigation: 'Lab / Scan',
  medication: 'Medication',
};

//  A date, or nothing. `last_applied_at` is null on a routine that was promoted but
//  never run, and "Invalid Date" is worse than a dash.
const when = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

//  ── THE THREE STATES A ROUTINE CAN BE IN ────────────────────────────────────
//  Premium and applied are independent: a routine can be promoted before it has ever
//  run, and one can be run every month without anybody promoting it. So this is not a
//  single ladder, and the panel says which of the two facts is true rather than
//  inventing a rank.
const stateOf = (r) => {
  if (r.premium) return { key: 'premium', label: 'Premium', tone: T.green, bg: T.greenSoft };
  if (r.used_count > 0) return { key: 'applied', label: 'In use', tone: T.inkSoft, bg: T.head };
  return { key: 'draft', label: 'Draft', tone: T.caution, bg: '#FFF8EB' };
};

const FILTERS = [
  ['all', 'All'],
  ['premium', 'Premium'],
  ['applied', 'In use'],
  ['draft', 'Drafts'],
];

export default function ClaimRoutines() {
  const [rows, setRows] = useState(null);          // null = loading
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [tab, setTab] = useState('all');
  const [open, setOpen] = useState(() => new Set());
  const [busyId, setBusyId] = useState(null);

  //  `all=1` lifts the shelf filter, so drafts are included — see the header comment.
  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await axios.get(claimsMultiVetRoutines, { params: { all: 1 } });
      setRows(data?.routines || []);
    } catch (e) {
      setRows([]);
      setError(e?.response?.data?.error || e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (rows || [])
      .filter((r) => (tab === 'all' ? true : stateOf(r).key === tab))
      .filter((r) => {
        if (!needle) return true;
        //  Searched by NAME and by what it contains. The name carries the pattern —
        //  attendance, age band, gender, condition — and the items carry the drugs, and
        //  either is a reasonable thing to remember a routine by.
        const hay = [r.name, ...(r.items || []).map((i) => `${i.code} ${i.name || ''}`)]
          .join(' ').toLowerCase();
        return hay.includes(needle);
      });
  }, [rows, tab, q]);

  const counts = useMemo(() => {
    const c = { all: (rows || []).length, premium: 0, applied: 0, draft: 0 };
    for (const r of rows || []) c[stateOf(r).key] += 1;
    return c;
  }, [rows]);

  //  ── PROMOTING AND DEMOTING ────────────────────────────────────────────────
  //  The same endpoint Multi Vet saves through. `items` has to travel because the write
  //  is an upsert on (name, facility) and the endpoint refuses an empty basket — so the
  //  routine is re-sent as it stands, with only `premium` changing.
  //
  //  `demote: true` is what lets premium come DOWN. Without it the endpoint keeps the
  //  higher of the two values, which is what stops a routine being demoted every time it
  //  is applied — see the GREATEST in the upsert.
  const setPremium = async (r, premium) => {
    setBusyId(r.id);
    try {
      await axios.post(claimsMultiVetRoutines, {
        name: r.name,
        items: r.items,
        cohort: r.cohort || null,
        facility: r.facility_id || null,
        premium,
        demote: !premium,
      });
      await load();
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: premium ? 'Could not make it premium' : 'Could not take it off premium',
        text: e?.response?.data?.error || e.message,
      });
    } finally { setBusyId(null); }
  };

  const remove = async (r) => {
    const ok = await Swal.fire({
      icon: 'warning',
      title: 'Delete this routine?',
      html: `<div style="text-align:left;font-size:13px;line-height:1.7">`
          + `<div style="font-weight:800">${String(r.name).replace(/</g, '&lt;')}</div>`
          + `<div style="margin-top:8px">${(r.items || []).length} item(s)`
          + (r.used_count ? `, applied ${r.used_count} time(s)` : ', never applied')
          + `.</div>`
          //  What it does NOT do. A routine is a saved intention, not the claims — and
          //  somebody about to delete one that has been applied 9 times deserves to know
          //  the 9 runs stand.
          + `<div style="margin-top:8px">The claims it was applied to are not affected. `
          + `Only the saved routine goes.</div></div>`,
      showCancelButton: true,
      confirmButtonText: 'Delete it',
      cancelButtonText: 'Keep',
      focusCancel: true,
    });
    if (!ok.isConfirmed) return;
    setBusyId(r.id);
    try {
      await axios.delete(`${claimsMultiVetRoutines}/${r.id}`);
      await load();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Could not delete it',
        text: e?.response?.data?.error || e.message });
    } finally { setBusyId(null); }
  };

  const toggle = (id) => setOpen((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  return (
    <div style={{
      minHeight: '100%', background: T.canvas, padding: '14px 16px 28px',
      color: T.ink,
    }}>
      {/*  ── THE HEADER ─────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap',
        marginBottom: 14,
      }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, letterSpacing: '-.01em' }}>
          Claim Routines
        </h2>
        <span style={{ fontSize: 13, color: T.inkMuted, lineHeight: 1.6 }}>
          The treatment routines this facility has built. Multi Vet saves one every time a
          basket is applied, named after the pattern it was applied to.
        </span>
        <button type="button" onClick={load}
          title="Read them again from the database"
          style={{
            marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '7px 12px', borderRadius: 8, cursor: 'pointer', font: 'inherit',
            fontSize: 12, fontWeight: 700, background: '#fff',
            border: `1px solid ${T.line}`, color: T.inkSoft,
          }}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/*  ── STATE TABS AND SEARCH ──────────────────────────────────────────── */}
      <div style={{
        display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
        marginBottom: 12,
      }}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {FILTERS.map(([k, label]) => {
            const on = tab === k;
            return (
              <button key={k} type="button" aria-pressed={on}
                onClick={() => setTab(k)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '5px 11px', borderRadius: 999, font: 'inherit',
                  fontSize: 12, fontWeight: on ? 800 : 650, cursor: 'pointer',
                  border: `1px solid ${on ? T.green : T.line}`,
                  background: on ? T.green : '#fff',
                  color: on ? '#fff' : T.inkSoft,
                }}>
                {label}
                <span style={{
                  fontSize: 10, fontWeight: 800, opacity: on ? 0.85 : 0.6, ...MONO,
                }}>{counts[k]}</span>
              </button>
            );
          })}
        </div>
        <div style={{ position: 'relative', flex: 1, minWidth: 220, maxWidth: 420 }}>
          <Search size={13} style={{
            position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)',
            color: T.inkFaint, pointerEvents: 'none',
          }} />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, or by a drug or diagnosis inside it…"
            style={{
              width: '100%', boxSizing: 'border-box', padding: '7px 10px 7px 27px',
              borderRadius: 8, font: 'inherit', fontSize: 12.5, background: '#fff',
              border: `1px solid ${T.line}`, color: T.ink,
            }} />
        </div>
      </div>

      {error && (
        <div style={{
          padding: '10px 13px', borderRadius: 8, marginBottom: 12, fontSize: 12.5,
          background: T.dangerSoft, border: `1px solid ${T.danger}44`, color: T.danger,
        }}>{error}</div>
      )}

      {/*  ── THE LIST ───────────────────────────────────────────────────────── */}
      {rows === null ? (
        <div style={{ fontSize: 13, color: T.inkMuted }}>Reading the routines…</div>
      ) : shown.length === 0 ? (
        <div style={{
          background: '#fff', border: `1px solid ${T.line}`, borderRadius: 12,
          padding: '28px 20px', textAlign: 'center',
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>
            {(rows || []).length === 0
              ? 'No routines saved yet'
              : 'Nothing here matches'}
          </div>
          <div style={{ fontSize: 12.5, color: T.inkMuted, lineHeight: 1.7 }}>
            {(rows || []).length === 0
              //  Says where they come from. An empty library with no explanation reads as
              //  a broken screen rather than one waiting for its first entry.
              ? <>Open <b>Claims → Multi Vet</b>, describe a group, build a basket and
                  apply it. The routine is saved automatically, named after the pattern —
                  and you are asked whether it should be premium.</>
              : <>Try a different search, or switch to <b>All</b>.</>}
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {shown.map((r) => {
            const st = stateOf(r);
            const isOpen = open.has(r.id);
            const items = r.items || [];
            const ran = when(r.last_applied_at);
            return (
              <div key={r.id} style={{
                background: '#fff', borderRadius: 12, minWidth: 0,
                border: `1px solid ${r.premium ? `${T.green}55` : T.line}`,
                //  A premium routine carries a rail rather than a fill: the row already
                //  uses background for its expanded state, and these are two different
                //  facts about it.
                borderLeft: `3px solid ${r.premium ? T.green : 'transparent'}`,
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px',
                  minWidth: 0, flexWrap: 'wrap',
                }}>
                  <Star size={14} style={{ flexShrink: 0 }}
                    fill={r.premium ? T.green : 'none'}
                    color={r.premium ? T.green : T.inkFaint} />

                  <span style={{ flex: 1, minWidth: 180 }}>
                    <span style={{
                      display: 'block', fontSize: 13.5,
                      fontWeight: r.premium ? 800 : 700, color: T.ink,
                    }}>{r.name}</span>
                    {/*  Who it was built for. Applying a paediatric routine to adults is
                         what the age-band guard refuses on the server; a library that
                         cannot say who a routine is for invites the attempt. */}
                    <span style={{
                      display: 'block', fontSize: 11.5, color: T.inkMuted, marginTop: 2,
                    }}>
                      {r.cohort?.summary?.length
                        ? `Built for ${r.cohort.summary.join(' · ')}`
                        : 'No pattern recorded'}
                      {r.actor ? ` · saved by ${r.actor}` : ''}
                    </span>
                  </span>

                  <span style={{
                    flexShrink: 0, padding: '3px 9px', borderRadius: 999,
                    fontSize: 10.5, fontWeight: 800, letterSpacing: '.04em',
                    textTransform: 'uppercase',
                    background: st.bg, color: st.tone,
                    border: `1px solid ${st.tone}33`,
                  }}>{st.label}</span>

                  {/*  How often, and how recently. Together they are what separates a
                       working routine from one that was right two seasons ago. */}
                  <span style={{
                    flexShrink: 0, fontSize: 11.5, color: T.inkMuted, whiteSpace: 'nowrap',
                    ...MONO,
                  }}>
                    {items.length} item{items.length === 1 ? '' : 's'}
                    {r.used_count ? ` · ×${r.used_count}` : ' · never run'}
                    {ran ? ` · ${ran}` : ''}
                  </span>

                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button type="button"
                      disabled={busyId === r.id}
                      onClick={() => setPremium(r, !r.premium)}
                      title={r.premium
                        ? 'Take it off premium — it stays saved'
                        : 'Make it premium, so it is offered first'}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '5px 10px', borderRadius: 7, font: 'inherit',
                        fontSize: 11.5, fontWeight: 700,
                        cursor: busyId === r.id ? 'wait' : 'pointer',
                        border: `1px solid ${r.premium ? T.line : `${T.green}55`}`,
                        background: r.premium ? '#fff' : T.greenSoft,
                        color: r.premium ? T.inkSoft : T.green,
                      }}>
                      <Star size={11} fill={r.premium ? 'none' : T.green}
                        color={r.premium ? T.inkSoft : T.green} />
                      {r.premium ? 'Un-premium' : 'Make premium'}
                    </button>
                    <button type="button"
                      disabled={busyId === r.id}
                      onClick={() => remove(r)}
                      aria-label={`Delete ${r.name}`}
                      title="Delete this routine"
                      style={{
                        display: 'inline-flex', alignItems: 'center',
                        padding: '5px 8px', borderRadius: 7, font: 'inherit',
                        cursor: busyId === r.id ? 'wait' : 'pointer',
                        border: `1px solid ${T.danger}44`,
                        background: T.dangerSoft, color: T.danger,
                      }}>
                      <Trash2 size={12} />
                    </button>
                    <button type="button"
                      onClick={() => toggle(r.id)}
                      aria-expanded={isOpen}
                      aria-label={isOpen ? 'Hide what it contains' : 'Show what it contains'}
                      style={{
                        display: 'inline-flex', alignItems: 'center',
                        padding: '5px 7px', borderRadius: 7, cursor: 'pointer',
                        border: `1px solid ${T.line}`, background: '#fff', color: T.inkMuted,
                      }}>
                      {isOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>
                  </div>
                </div>

                {/*  ── WHAT IT CONTAINS ────────────────────────────────────────
                     Grouped by kind, in the order a claim is built: diagnosis,
                     procedure, lab, medication. A medication shows its prescription,
                     because a drug without a dose is not a routine anybody can trust —
                     and it is the half most likely to have been left incomplete. */}
                {isOpen && (
                  <div style={{
                    borderTop: `1px solid ${T.line}`, background: T.canvas,
                    padding: '10px 14px 12px', display: 'grid', gap: 10,
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  }}>
                    {['diagnosis', 'procedure', 'investigation', 'medication'].map((k) => {
                      const of = items.filter((i) => i.kind === k);
                      if (!of.length) return null;
                      return (
                        <div key={k} style={{ minWidth: 0 }}>
                          <div style={{
                            fontSize: 9.5, fontWeight: 800, letterSpacing: '.06em',
                            textTransform: 'uppercase', color: T.inkFaint, marginBottom: 4,
                          }}>{KIND_LABEL[k]} · {of.length}</div>
                          <div style={{ display: 'grid', gap: 4 }}>
                            {of.map((i, n) => (
                              <div key={`${i.code}-${n}`} style={{ minWidth: 0 }}>
                                <div style={{
                                  fontSize: 12, color: T.ink, overflow: 'hidden',
                                  textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }} title={`${i.code} ${i.name || ''}`}>
                                  <span style={{ ...MONO, fontSize: 11, color: T.inkFaint }}>
                                    {i.code}
                                  </span>{' '}{i.name || ''}
                                </div>
                                {k === 'medication' && (
                                  <div style={{
                                    fontSize: 11, color: T.inkMuted, lineHeight: 1.5,
                                  }}>
                                    {[i.dose, i.frequency,
                                      i.duration ? `${i.duration} day(s)` : null,
                                      i.route,
                                      i.quantity ? `qty ${i.quantity}` : null]
                                      .filter(Boolean).join(' · ') || (
                                      <span style={{ color: T.danger }}>
                                        no prescription saved
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    {items.length === 0 && (
                      <div style={{ fontSize: 12, color: T.danger }}>
                        This routine has no items saved.
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/*  ── WHAT THIS SCREEN DOES NOT DO ───────────────────────────────────── */}
      {rows !== null && rows.length > 0 && (
        <div style={{
          marginTop: 14, fontSize: 11.5, color: T.inkMuted, lineHeight: 1.7,
        }}>
          Applying a routine needs claims to apply it to, so that stays in{' '}
          <b>Claims → Multi Vet</b>: describe a group, and the premium routines are
          offered there first.
        </div>
      )}
    </div>
  );
}
